import React, { useState, useRef, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import { usePlayer } from '../context/PlayerContext';

const {
  FiMusic, FiPlay, FiPause, FiSkipBack, FiSkipForward,
  FiVolume2, FiVolumeX, FiX,
} = FiIcons;

const Player = () => {
  const { currentTrack, isPlaying, togglePlay, clearTrack } = usePlayer();
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(33);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setProgress(p => (p >= 100 ? 0 : p + 0.2));
      }, 200);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPlaying]);

  if (!currentTrack) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 glass-panel border-t border-white/8">
      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-white/5">
        <div
          className="h-full bg-gradient-to-r from-brand-pink to-brand-coral transition-all duration-200"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 h-20 flex items-center gap-4 md:gap-8">

        {/* Track info */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-11 h-11 rounded-xl overflow-hidden flex-shrink-0 border border-white/10 bg-zinc-900">
            {currentTrack.releaseContext?.cover ? (
              <img src={currentTrack.releaseContext.cover} alt="cover"
                className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <SafeIcon icon={FiMusic} className="text-zinc-600 text-base" />
              </div>
            )}
          </div>
          <div className="hidden sm:block min-w-0">
            <p className="text-xs font-black uppercase tracking-tight text-white truncate">
              {currentTrack.title}
            </p>
            <p className="text-[10px] font-bold text-zinc-500 truncate">
              {currentTrack.releaseContext?.artist || 'Unknown Artist'}
            </p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-4 flex-shrink-0">
          <button className="text-zinc-600 hover:text-white transition-colors hidden sm:block">
            <SafeIcon icon={FiSkipBack} className="text-lg" />
          </button>
          <button
            onClick={togglePlay}
            className="w-11 h-11 btn-brand rounded-full flex items-center justify-center flex-shrink-0"
          >
            <SafeIcon icon={isPlaying ? FiPause : FiPlay} className="text-base" />
          </button>
          <button className="text-zinc-600 hover:text-white transition-colors hidden sm:block">
            <SafeIcon icon={FiSkipForward} className="text-lg" />
          </button>
        </div>

        {/* Volume + close */}
        <div className="flex items-center gap-3 flex-1 justify-end">
          <button
            onClick={() => setMuted(m => !m)}
            className="text-zinc-600 hover:text-white transition-colors hidden md:block"
          >
            <SafeIcon icon={muted ? FiVolumeX : FiVolume2} className="text-base" />
          </button>
          <input
            type="range" min="0" max="1" step="0.01"
            value={muted ? 0 : volume}
            onChange={e => { setVolume(+e.target.value); setMuted(false); }}
            className="w-20 hidden md:block accent-brand-pink"
          />
          <button
            onClick={clearTrack}
            className="w-8 h-8 rounded-xl glass-subtle flex items-center justify-center text-zinc-600 hover:text-white transition-colors"
          >
            <SafeIcon icon={FiX} className="text-xs" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Player;